import { API_URL } from '../constants/constants';
import { auth } from '../firebase';
import logger from './logger';

const normalizeBase = (url) => String(url || '').replace(/\/+$/, '');

async function getAuthToken() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be logged in');
  }
  return user.getIdToken();
}

/**
 * Authenticated fetch wrapper for /api/receipts/* endpoints.
 */
export async function receiptsRequest(path, { method = 'GET', body, headers = {}, responseType = 'json' } = {}) {
  const base = normalizeBase(process.env.REACT_APP_API_URL || API_URL);
  const urlPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${urlPath}`;

  const token = await getAuthToken();
  const init = {
    method,
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      ...headers
    }
  };

  if (body !== undefined && body !== null) {
    if (body instanceof FormData) {
      init.body = body;
    } else if (typeof body === 'string') {
      init.headers['Content-Type'] = init.headers['Content-Type'] || 'text/plain';
      init.body = body;
    } else {
      init.headers['Content-Type'] = init.headers['Content-Type'] || 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  const response = await fetch(url, init);

  if (response.status === 204) {
    return null;
  }

  if (responseType === 'blob') {
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || response.statusText);
    }
    return response.blob();
  }

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message = typeof data === 'object' && data?.error ? data.error : response.statusText;
    logger.error('receiptsRequest failed', { url, status: response.status, message });
    throw new Error(message || 'Request failed');
  }

  return data;
}

export const receiptsApi = {
  seedSettings: () => receiptsRequest('/api/receipts/settings/seed', { method: 'POST' }),

  getStats: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return receiptsRequest(`/api/receipts/stats${qs ? `?${qs}` : ''}`);
  },

  listUnsortedFiles: (limit = 200, params = {}) => {
    const query = {
      limit: String(limit),
      ...params
    };
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(query).filter(([, v]) => v !== '' && v !== null && v !== undefined))
    ).toString();
    return receiptsRequest(`/api/receipts/files/unsorted?${qs}`);
  },

  registerFile: (payload) =>
    receiptsRequest('/api/receipts/files', { method: 'POST', body: payload }),

  getFileUrl: (fileId) => receiptsRequest(`/api/receipts/files/${fileId}/url`),

  /** Stream file bytes through API (avoids GCS signed-URL / signBlob IAM requirements). */
  getFileContent: async (fileId) => {
    const base = normalizeBase(process.env.REACT_APP_API_URL || API_URL);
    const token = await getAuthToken();
    const response = await fetch(`${base}/api/receipts/files/${fileId}/content`, {
      method: 'GET',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text || response.statusText;
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) {
          message = parsed.error;
        }
      } catch {
        // keep text
      }
      logger.error('getFileContent failed', { fileId, status: response.status, message });
      throw new Error(message || 'Failed to load receipt file');
    }
    return {
      blob: await response.blob(),
      contentType: response.headers.get('content-type') || 'application/octet-stream'
    };
  },

  deleteFile: (fileId) =>
    receiptsRequest(`/api/receipts/files/${fileId}`, { method: 'DELETE' }),

  analyzeFile: (fileId) =>
    receiptsRequest(`/api/receipts/files/${fileId}/analyze`, { method: 'POST' }),

  confirmFile: (fileId, payload) =>
    receiptsRequest(`/api/receipts/files/${fileId}/confirm`, { method: 'POST', body: payload }),

  listTransactions: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== null))
    ).toString();
    return receiptsRequest(`/api/receipts/transactions${qs ? `?${qs}` : ''}`);
  },

  getTransaction: (id) => receiptsRequest(`/api/receipts/transactions/${id}`),

  createTransaction: (payload) =>
    receiptsRequest('/api/receipts/transactions', { method: 'POST', body: payload }),

  updateTransaction: (id, payload) =>
    receiptsRequest(`/api/receipts/transactions/${id}`, { method: 'PUT', body: payload }),

  deleteTransaction: (id) =>
    receiptsRequest(`/api/receipts/transactions/${id}`, { method: 'DELETE' }),

  listSettings: () => receiptsRequest('/api/receipts/settings'),

  updateSetting: (code, payload) =>
    receiptsRequest(`/api/receipts/settings/${code}`, { method: 'PUT', body: payload }),

  listCategories: () => receiptsRequest('/api/receipts/settings/categories'),
  saveCategory: (payload) =>
    receiptsRequest('/api/receipts/settings/categories', { method: 'POST', body: payload }),
  updateCategory: (code, payload) =>
    receiptsRequest(`/api/receipts/settings/categories/${code}`, { method: 'PUT', body: payload }),
  deleteCategory: (code) =>
    receiptsRequest(`/api/receipts/settings/categories/${code}`, { method: 'DELETE' }),

  listProjects: () => receiptsRequest('/api/receipts/settings/projects'),
  saveProject: (payload) =>
    receiptsRequest('/api/receipts/settings/projects', { method: 'POST', body: payload }),
  updateProject: (code, payload) =>
    receiptsRequest(`/api/receipts/settings/projects/${code}`, { method: 'PUT', body: payload }),
  deleteProject: (code) =>
    receiptsRequest(`/api/receipts/settings/projects/${code}`, { method: 'DELETE' }),

  listFields: () => receiptsRequest('/api/receipts/settings/fields'),
  saveField: (payload) =>
    receiptsRequest('/api/receipts/settings/fields', { method: 'POST', body: payload }),
  updateField: (code, payload) =>
    receiptsRequest(`/api/receipts/settings/fields/${code}`, { method: 'PUT', body: payload }),
  deleteField: (code) =>
    receiptsRequest(`/api/receipts/settings/fields/${code}`, { method: 'DELETE' }),

  listCurrencies: () => receiptsRequest('/api/receipts/settings/currencies'),
  saveCurrency: (payload) =>
    receiptsRequest('/api/receipts/settings/currencies', { method: 'POST', body: payload }),
  updateCurrency: (code, payload) =>
    receiptsRequest(`/api/receipts/settings/currencies/${code}`, { method: 'PUT', body: payload }),
  deleteCurrency: (code) =>
    receiptsRequest(`/api/receipts/settings/currencies/${code}`, { method: 'DELETE' }),

  exportCsv: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries({ ...params, format: 'csv' }).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      )
    ).toString();
    return receiptsRequest(`/api/receipts/import-export/export${qs ? `?${qs}` : ''}`, {
      responseType: 'blob'
    });
  },

  exportZip: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(
        Object.entries({ ...params, format: 'zip' }).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      )
    ).toString();
    return receiptsRequest(`/api/receipts/import-export/export${qs ? `?${qs}` : ''}`, {
      responseType: 'blob'
    });
  },

  importCsv: (csv) =>
    receiptsRequest('/api/receipts/import-export/import', { method: 'POST', body: { csv } })
};

export default receiptsApi;
